import { createRoot, Root } from 'react-dom/client';
import { ComponentType } from 'react';

import { IslandRoot, ensureIslandRuntime } from './IslandRoot';
import { ISLANDS } from './registry';
import { IslandName, IslandPropsMap } from './props';

export type {
  IslandName,
  IslandPropsMap,
  IslandPropsFor,
  CollapseAllButtonProps,
  TrialInfoHeaderProps,
} from './props';
export { configureIslandRouter } from './shims/next-router';
export type { IslandNavigation } from './shims/next-router';

export interface IslandHandle<P> {
  update(props: P): void;
  unmount(): void;
}

/*
 * Mount one of the already registered islands.
 */
export function mountIsland<N extends IslandName>(
  el: HTMLElement,
  name: N,
  props: IslandPropsMap[N]
): IslandHandle<IslandPropsMap[N]> {
  const root: Root = createRoot(el);

  let Component: ComponentType<IslandPropsMap[N]> | null = null;
  let currentProps: IslandPropsMap[N] = props;

  const render = () => {
    if (!Component) {
      return;
    }

    root.render(
      <IslandRoot host={el}>
        <Component {...currentProps} />
      </IslandRoot>
    );
  };

  // The locale catalogue and the component chunk both load asynchronously.
  Promise.all([ensureIslandRuntime(), ISLANDS[name]()])
    .then(([, component]) => {
      Component = component;
      render();
    })
    .catch((error: unknown) => {
      console.error(`Failed to mount island "${name}"`, error);
    });

  return {
    update(next: IslandPropsMap[N]) {
      currentProps = next;
      render();
    },
    unmount() {
      Component = null;
      root.unmount();
    },
  };
}
